import React, { useCallback, useEffect, useRef } from "react";

export default function WaveformBar({
  peaks,
  duration,
  currentTime,
  onSeek,
  fallbackProgress,
}) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;

    const dpr = window.devicePixelRatio || 1;
    const width = wrap.clientWidth;
    const height = 72;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    const progress =
      duration > 0 ? currentTime / duration : fallbackProgress || 0;
    const barW = peaks?.length ? width / peaks.length : 0;

    if (peaks?.length) {
      const gap = 1;
      for (let i = 0; i < peaks.length; i += 1) {
        const x = i * barW;
        const h = Math.max(2, peaks[i] * (height - 8));
        const y = (height - h) / 2;
        const played = i / peaks.length < progress;
        ctx.fillStyle = played ? "#5b9cff" : "rgba(148, 163, 184, 0.45)";
        ctx.fillRect(x, y, Math.max(1, barW - gap), h);
      }
    } else {
      ctx.fillStyle = "rgba(148, 163, 184, 0.25)";
      ctx.fillRect(0, height / 2 - 2, width, 4);
      ctx.fillStyle = "#5b9cff";
      ctx.fillRect(0, height / 2 - 2, width * progress, 4);
    }

    if (duration > 0) {
      const x = progress * width;
      ctx.strokeStyle = "#f8fafc";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
    }
  }, [peaks, duration, currentTime, fallbackProgress]);

  useEffect(() => {
    draw();
  }, [draw]);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return undefined;
    const ro = new ResizeObserver(() => draw());
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [draw]);

  const handlePointer = (clientX) => {
    const wrap = wrapRef.current;
    if (!wrap || !duration) return;
    const rect = wrap.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    onSeek(ratio * duration);
  };

  return (
    <div
      ref={wrapRef}
      className="ss-waveform"
      role="slider"
      aria-valuemin={0}
      aria-valuemax={duration || 0}
      aria-valuenow={currentTime}
      aria-label="Playback position"
      tabIndex={0}
      onClick={(e) => handlePointer(e.clientX)}
      onKeyDown={(e) => {
        if (e.key === "ArrowLeft") onSeek(Math.max(0, currentTime - 5));
        if (e.key === "ArrowRight") onSeek(Math.min(duration, currentTime + 5));
      }}
    >
      <canvas ref={canvasRef} />
    </div>
  );
}
